import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/utils/errorLogger';

import { createCRUDEntity } from '../crud-factory';
import { transformInquiryData } from '../transformations';

type SupabaseError = {
  code?: string;
  message?: string;
};

type InquiryRecord = {
  user_id?: string;
  [key: string]: unknown;
};

// Inquiry Entity with custom list method for profile joining
const INQUIRY_SELECT_COLUMNS = `
  id,
  clinic_id,
  patient_name,
  patient_email,
  phone,
  country,
  status,
  created_at,
  updated_at,
  user_id,
  treatment_interest,
  message,
  selected_branch_name,
  selected_branch_city,
  selected_branch_district,
  selected_treatment_type,
  selected_doctor_ids,
  age,
  gender,
  height_cm,
  weight_kg,
  blood_type,
  allergies,
  current_medications,
  chronic_conditions,
  past_surgeries,
  smoking_status,
  alcohol_consumption,
  exercise_frequency,
  family_history,
  preferred_languages,
  admin_comments
`.replaceAll(/\s+/g, ' ');

const INQUIRY_FALLBACK_COLUMNS = `
  id,
  clinic_id,
  patient_name,
  patient_email,
  phone,
  country,
  status,
  created_at,
  updated_at,
  user_id,
  treatment_interest,
  message,
  selected_branch_name,
  age,
  gender,
  height_cm,
  weight_kg,
  blood_type,
  allergies,
  current_medications,
  chronic_conditions,
  past_surgeries,
  smoking_status,
  alcohol_consumption,
  exercise_frequency,
  family_history,
  admin_comments
`.replaceAll(/\s+/g, ' ');

const fetchInquiriesWithFallback = async (
  executeInquiryQuery: (columns: string) => Promise<{ data: unknown; error: SupabaseError | null }>
) => {
  let { data: inquiries, error } = await executeInquiryQuery(INQUIRY_SELECT_COLUMNS);

  if (error) {
    if (error.code === '42703') {
      logError(
        new Error(
          `[Inquiry.list] Falling back to legacy column set due to missing column: ${error.message}`
        ),
        { source: 'Inquiry.list fallback' }
      );
      const fallbackResult = await executeInquiryQuery(INQUIRY_FALLBACK_COLUMNS);
      if (fallbackResult.error) {
        throw new Error(fallbackResult.error.message ?? 'Database query failed');
      }
      return fallbackResult.data ?? [];
    }
    throw new Error(error.message ?? 'Database query failed');
  }

  return inquiries ?? [];
};

const fetchProfileImages = async (userIds: string[]): Promise<Record<string, string | null>> => {
  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('id, profile_image_url')
    .in('id', userIds);

  if (!profileError) {
    return profiles.reduce(
      (acc, profile) => {
        acc[profile.id] = profile.profile_image_url;
        return acc;
      },
      {} as Record<string, string | null>
    );
  }

  return {} as Record<string, string | null>;
};

export const Inquiry = createCRUDEntity('inquiries', transformInquiryData, {
  customMethods: {
    // Override list to include profile image joining
    list: async (orderBy = '-created_at') => {
      const orderField = orderBy.startsWith('-') ? orderBy.slice(1) : orderBy;

      const executeInquiryQuery = async (columnSelection: string) => {
        return supabase
          .from('inquiries')
          .select(columnSelection)
          .order(orderField, {
            ascending: orderBy.startsWith('-') ? false : true,
          });
      };

      let inquiries = (await fetchInquiriesWithFallback(executeInquiryQuery)) as InquiryRecord[];
      const userIds = inquiries
        .filter((inquiry: InquiryRecord) => inquiry.user_id)
        .map((inquiry: InquiryRecord) => inquiry.user_id!);

      if (userIds.length > 0) {
        const profileMap = await fetchProfileImages(userIds);
        return inquiries.map((inquiry: InquiryRecord) => ({
          ...inquiry,
          profile_image_url: inquiry.user_id ? (profileMap[inquiry.user_id] ?? null) : null,
        }));
      }

      return inquiries.map((inquiry: InquiryRecord) => ({
        ...inquiry,
        profile_image_url: null,
      }));
    },
  },
});
